import { useState, Children, cloneElement, useEffect } from 'react'
import { useSelector } from 'react-redux'
import loadable from '@loadable/component'

// Components
import Footer from 'components/interestform/footer'

// Types
import { State } from 'modules/types'

// Utils
import { VIRTUALPAGE_NAMES } from 'components/interestform/utils'
import { TRACKING_ENABLED } from '@constants'
import styles from './interestform.css'

// Loadable
const FormikLoadable = loadable.lib(() => import('formik'))

type MultiStepFormProps = {
  initialValues: Record<string, unknown>
  onSubmit: (values: Record<string, unknown>, bag: Record<string, unknown>) => Promise<Record<string, unknown>>
  children: JSX.Element[]
}

const MultiStepForm = (props: MultiStepFormProps): JSX.Element => {
  const { initialValues, onSubmit, children } = props

  const [stepIndexNumber, setStepIndexNumber] = useState(0)
  const [snapshot, setSnapshot] = useState(initialValues)
  const steps = Children.toArray(children)

  const step = steps[stepIndexNumber]
  const totalSteps = steps.length
  const isLastStep = stepIndexNumber === totalSteps - 2

  const footerHidden = () => {
    const hiddenPages = [0, 1, 19]
    return hiddenPages.includes(stepIndexNumber)
  }

  const next = (values: Record<string, unknown>) => {
    setSnapshot(values)
    setStepIndexNumber(Math.min(stepIndexNumber + 1, totalSteps - 1))
  }

  const previous = (values: Record<string, unknown>) => {
    const { skipped_presentation } = values
    setSnapshot(values)
    if (skipped_presentation && stepIndexNumber === 14) {
      return setStepIndexNumber(Math.max(1, 0))
    }
    return setStepIndexNumber(Math.max(stepIndexNumber - 1, 0))
  }

  const handleBackToSlides = () => {
    return setStepIndexNumber(Math.max(1, 0))
  }

  const handleSkippedPresentation = (values: Record<string, unknown>) => {
    setSnapshot(values)
    setStepIndexNumber(14)
  }

  const handleFormSubmit = async (values: Record<string, unknown>, bag: Record<string, unknown>) => {
    const { skipped_presentation } = values
    if (step.props.onSubmit) {
      await step.props.onSubmit(values, bag)
    }
    if (skipped_presentation && stepIndexNumber === 1) {
      return handleSkippedPresentation(values)
    }
    if (isLastStep) {
      await onSubmit(values, bag)
      return next(values)
    } else {
      bag.setTouched({})
      return next(values)
    }
  }

  const pathname = useSelector<State>(state => state.router.location.pathname)
  const virtualPagePath = `${pathname}/${VIRTUALPAGE_NAMES[stepIndexNumber as keyof typeof VIRTUALPAGE_NAMES]}`

  useEffect(() => {
    if (TRACKING_ENABLED) {
      ga('set', 'page', virtualPagePath)
      ga('send', 'pageview')
      gtag('event', 'page_view', {
        page_path: virtualPagePath,
      })
    }
  }, [virtualPagePath])

  return (
    <FormikLoadable>
      {({ Formik, Form: FormikForm }) => (
        <Formik initialValues={snapshot} onSubmit={handleFormSubmit}>
          {({ isSubmitting, values, handleSubmit, setFieldValue, errors, touched }) => {
            return (
              <FormikForm className={styles.formWrapper}>
                {cloneElement(step, {
                  handleSubmit,
                  setFieldValue,
                  values,
                  errors,
                  touched,
                })}
                {!footerHidden() && (
                  <Footer
                    handlePrevious={previous}
                    handleNext={handleSubmit}
                    handleBackToSlides={handleBackToSlides}
                    isSubmitting={isSubmitting}
                    isLastStep={isLastStep}
                    stepData={{
                      stepIndexNumber,
                      totalSteps,
                    }}
                    values={values}
                    touched={touched}
                  />
                )}
              </FormikForm>
            )
          }}
        </Formik>
      )}
    </FormikLoadable>
  )
}

export default MultiStepForm
